<template>
  <!-- 底部操作栏：负责提交、撤销接口调用与请求中的操作锁定。 -->
  <footer>
    <div class="progress">
      <span>已判断 {{ summary.done }} / {{ summary.total }} 项<span v-if="summary.fail">· 不合格 {{ summary.fail }} 项</span></span>
      <AProgress :percent="progressPercent" :show-info="false" size="small" />
    </div>
    <div class="actions">
      <AButton :disabled="submitted || loading" @click="temporarySave">暂存</AButton>
      <AButton type="primary" :disabled="loading" @click="submitted ? revokeTask() : $emit('request-submit')">
        {{ submitted ? '撤销' : '提交检验' }}
      </AButton>
    </div>
    <XModal ref="submitModal" title="确认提交检验" :width="520" :ok="submitTask">
      <p>共 {{ summary.total }} 项，合格 {{ summary.total - summary.fail }} 项，不合格 {{ summary.fail }} 项。</p>
      <p class="muted">提交后本次判断锁定；每道工序的检验记录独立保留。</p>
    </XModal>
  </footer>
</template>

<script>
import { mapMutations } from 'vuex'
import { inspectionPatrolTaskSubmitWithFiles, itemporarySaveWithFiles, patrolTaskRevoke } from '@api'

export default {
  name: "InspectionFooter",
  props: {
    summary: {
      type: Object,
      required: true,
    },
    progressPercent: {
      type: Number,
      required: true,
    },
    submitted: {
      type: Boolean,
      default: false,
    },
    taskId: {
      type: [Number, String],
      default: null,
    },
    results: {
      type: Array,
      default: () => [],
    },
    // 当前检验的补充备注，提交和暂存时一并保存。
    remark: {
      type: String,
      default: "",
    },
    // 包含已上传附件 ID 与本次待上传图片文件的附件列表。
    evidence: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  methods: {
    ...mapMutations(["setRefresh"]),
    // 父页面校验通过后直接调用，打开提交确认窗口。
    openSubmitModal() {
      this.$refs.submitModal.open();
    },
    // 暂存与提交使用相同参数，暂存可只保存当前已判断的检验项。
    async temporarySave() {
      if (this.loading || !this.taskId) return;

      this.setLoading(true);
      try {
        const response = await itemporarySaveWithFiles(this.buildSubmitFormData());
        this.$emit("saved", response);
        this.$message.success("检验结果已暂存。");
        this.setRefresh()
      } catch (error) {
        this.$message.error("检验结果暂存失败，请稍后重试。");
      } finally {
        this.setLoading(false);
      }
    },
    async submitTask() {
      if (this.loading || !this.taskId) return false;
      if (this.results.length !== this.summary.total) {
        this.$message.error("检验项数据不完整，无法提交。");
        return false;
      }

      this.setLoading(true);
      try {
        const response = await inspectionPatrolTaskSubmitWithFiles(this.buildSubmitFormData());
        this.$emit("submitted", response);
        this.$message.success("检验提交完成。");
        this.setRefresh()
        return true;
      } catch (error) {
        this.$message.error("检验提交失败，请稍后重试。");
        return false;
      } finally {
        this.setLoading(false);
      }
    },
    // 提交和暂存统一使用带附件接口，认证头由 Axios 请求拦截器自动附加。
    buildSubmitFormData() {
      const formData = new FormData();
      const oldFileIds = this.evidence
        .map((item) => item.id)
        .filter((id) => id !== undefined && id !== null && id !== "");

      formData.append("taskId", this.taskId);
      formData.append("remark", this.remark || "");
      formData.append("results", JSON.stringify(this.results));
      formData.append("oldFileIds", oldFileIds.join(","));
      formData.append("attachmentSubmitted", "true");
      this.evidence
        .filter((item) => item.file)
        .forEach((item) => formData.append("newFile", item.file));
      return formData;
    },
    async revokeTask() {
      if (this.loading || !this.taskId) return;

      this.setLoading(true);
      try {
        const response = await patrolTaskRevoke(this.taskId);
        this.$emit("revoked", response);
        this.$message.success("检验任务已撤销。");
        this.setRefresh()
      } catch (error) {
        this.$message.error("检验任务撤销失败，请稍后重试。");
      } finally {
        this.setLoading(false);
      }
    },
    setLoading(value) {
      this.loading = value;
      this.$emit("loading-change", value);
    },
  },
};
</script>

<style lang="less" scoped>
footer,
.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.actions {
  flex-wrap: wrap;
}

// 底部暂存和提交入口统一放大，便于工位端操作。
.actions /deep/ .ant-btn {
  box-sizing: border-box;
  width: 120px;
  height: 44px;
  font-size: 16px;
}

footer {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 5;
  padding: 15px 24px;
  background: #fff;
  border-top: 1px solid #cfdae6;
  box-shadow: 0 -4px 20px #203c5610;
}

.progress {
  display: grid;
  gap: 6px;
  min-width: 210px;
}

.muted {
  color: #62758a;
  font-size: 14px;
}

@media (max-width: 700px) {
  footer {
    flex-wrap: wrap;
    padding: 12px 14px;
  }

  .progress,
  footer .actions {
    width: 100%;
  }

  footer .actions {
    justify-content: flex-end;
  }
}
</style>
